'use client';

import { c as _c } from "react/compiler-runtime";
import { type NotificationProps, Notification as TdsNotification } from '@tallinn-arvudes/ui/tallinn-design/Notification';
import { cn } from '@tallinn-arvudes/ui/utils';
import { type ComponentProps, createContext, type ReactNode, useContext, useState } from 'react';
interface NotificationState {
  message: ReactNode;
  type: ComponentProps<typeof TdsNotification>['variant'];
  autoDismiss?: number | boolean;
  actions?: ComponentProps<typeof TdsNotification>['actions'];
}
type NotificationContextType = {
  showNotification: (notification: NotificationState) => void;
  clearNotification: () => void;
  notification: NotificationState | null;
};
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};
export const NotificationProvider = t0 => {
  const $ = _c(7);
  const {
    children
  } = t0;
  const [notification, setNotification] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setNotification(null);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const clearNotification = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = notification_0 => {
      setNotification(notification_0);
      if (notification_0.autoDismiss) {
        setTimeout(() => {
          clearNotification();
        }, typeof notification_0.autoDismiss === "number" ? notification_0.autoDismiss : 4000);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const showNotification = t2;
  let t3;
  if ($[2] !== notification) {
    t3 = {
      notification,
      showNotification,
      clearNotification
    };
    $[2] = notification;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== t3) {
    t4 = <NotificationContext.Provider value={t3}>{children}</NotificationContext.Provider>;
    $[4] = children;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
export const Notification = ({
  onDismiss,
  className,
  successClassName,
  ...props
}: Omit<NotificationProps, 'title'> & {
  successClassName?: string;
}) => {
  const {
    notification,
    clearNotification
  } = useNotification();
  const handleNotificationDismiss = () => {
    clearNotification();
    onDismiss?.();
  };
  return notification ? <TdsNotification actions={notification?.actions} variant={notification?.type || 'neutral'} title={notification?.message || ''} className={cn('z-[60] fixed top-[3.28rem] md:top-[4.7rem] mt-2 left-0 right-0 mx-auto max-w-[90%] md:max-w-3xl', className, {
    [successClassName!]: notification?.type === 'success'
  })} onDismiss={handleNotificationDismiss} {...props} /> : null;
};